import { createContext, useContext, useState, useMemo, useCallback, useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthService } from '@/services/core/auth'
import { NetworksApi, Network } from '@/services/networks/networksApi'
import {
  getExtraNetworks,
  setExtraNetworks,
  getUserNetwork,
  setUserNetwork,
  MAX_EXTRA_NETWORKS,
} from '@/services/core/context'
import { useCurrentUser } from './UserContext'
import { configureRollbarPerson } from '@/services/core/rollbar'

/**
 * Query keys whose data is scoped by the extra-network selection, i.e. the
 * advertiser chain's list endpoints — the only ones the API's `network`
 * parameter is declared on. Toggling an extra network refetches exactly these.
 */
const SCOPED_QUERY_KEYS = ['lineitems', 'orders', 'advertisers', 'creatives'] as const

interface NetworkContextValue {
  networks: Network[]
  currentNetwork: Network | null
  loading: boolean
  /** Sets the primary network and reloads, optionally landing on `nextPath`. */
  switchNetwork: (tagId: string, nextPath?: string) => void
  /** Extra networks included in advertising list queries, by tagId. */
  extraNetworkTagIds: string[]
  /** The same networks resolved against the user's accessible networks. */
  extraNetworks: Network[]
  toggleExtraNetwork: (tagId: string) => void
  clearExtraNetworks: () => void
  /** How many more extras the API will accept. */
  extraNetworksRemaining: number
  /**
   * Stable identity of the whole network scope (primary + extras). Put this in
   * the queryKey of an advertising list so its cache entry is per-scope.
   */
  scopeKey: string
  /**
   * False until the network list has resolved. `scopeKey` is not final before
   * then, so gate an advertising list's `enabled` on this to avoid fetching it
   * once under a placeholder key and again under the real one.
   */
  scopeReady: boolean
}

const NetworkContext = createContext<NetworkContextValue>({
  networks: [],
  currentNetwork: null,
  loading: false,
  switchNetwork: () => {},
  extraNetworkTagIds: [],
  extraNetworks: [],
  toggleExtraNetwork: () => {},
  clearExtraNetworks: () => {},
  extraNetworksRemaining: MAX_EXTRA_NETWORKS,
  scopeKey: '',
  scopeReady: false,
})

export const NETWORK_STORAGE_KEY = 'adnuntius.network'
const STORAGE_KEY = NETWORK_STORAGE_KEY

export function NetworkProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser()
  const queryClient = useQueryClient()
  const authenticated = !!user || AuthService.isAuthenticated()

  const { data: allNetworks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['networks'],
    queryFn: NetworksApi.getAll,
    enabled: authenticated,
    staleTime: Infinity,
  })

  const networks = user
    ? allNetworks.filter((n) => !!user.permissionsByNetwork[n.tagId])
    : allNetworks

  const savedTagId = localStorage.getItem(STORAGE_KEY)
  const userTagId = user ? getUserNetwork(user.id) : null
  const matchedNetwork =
    networks.find((n) => n.tagId === savedTagId) ??
    networks.find((n) => n.tagId === userTagId) ??
    networks[0] ??
    null

  // Persist the resolved network — covers first-time load and post-masquerade
  // where the previously saved tagId is no longer accessible to the current user
  if (matchedNetwork && matchedNetwork.tagId !== savedTagId) {
    localStorage.setItem(STORAGE_KEY, matchedNetwork.tagId)
  }
  if (user && matchedNetwork) setUserNetwork(user.id, matchedNetwork.tagId)

  // Fetch full network details for the current network
  const currentTagId = matchedNetwork?.tagId
  const { data: currentNetwork = null, isLoading: detailLoading } = useQuery({
    queryKey: ['network', currentTagId],
    queryFn: () => NetworksApi.getByTagId(currentTagId!),
    enabled: authenticated && !!currentTagId,
    staleTime: Infinity,
  })

  useEffect(() => {
    configureRollbarPerson(user, currentNetwork)
  }, [user, currentNetwork])

  const [storedExtras, setStoredExtras] = useState<string[]>(getExtraNetworks)

  // The primary network is never also an extra, and a network the current user
  // has lost access to (masquerade, revoked role) drops out silently.
  const extraNetworkTagIds = useMemo(() => {
    if (!networks.length) return []
    return storedExtras.filter(
      (tagId) => tagId !== currentTagId && networks.some((n) => n.tagId === tagId),
    )
  }, [storedExtras, networks, currentTagId])

  const extraNetworks = useMemo(
    () => extraNetworkTagIds.map((tagId) => networks.find((n) => n.tagId === tagId)!),
    [extraNetworkTagIds, networks],
  )

  const applyExtras = useCallback(
    (next: string[]) => {
      setExtraNetworks(next)
      setStoredExtras(next)
      for (const key of SCOPED_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
    [queryClient],
  )

  const toggleExtraNetwork = useCallback(
    (tagId: string) => {
      if (tagId === currentTagId) return
      const current = getExtraNetworks()
      const next = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId]
      if (next.length > MAX_EXTRA_NETWORKS) return
      applyExtras(next)
    },
    [applyExtras, currentTagId],
  )

  const clearExtraNetworks = useCallback(() => applyExtras([]), [applyExtras])

  const switchNetwork = useCallback((tagId: string, nextPath?: string) => {
    localStorage.setItem(STORAGE_KEY, tagId)
    // A full load is what makes every network-scoped query re-resolve; assigning
    // the destination reloads and navigates in one step.
    if (nextPath) {
      window.location.assign(nextPath)
    } else {
      window.location.reload()
    }
  }, [])

  const value = useMemo<NetworkContextValue>(
    () => ({
      networks,
      currentNetwork,
      loading: networksLoading || detailLoading,
      switchNetwork,
      extraNetworkTagIds,
      extraNetworks,
      toggleExtraNetwork,
      clearExtraNetworks,
      extraNetworksRemaining: MAX_EXTRA_NETWORKS - extraNetworkTagIds.length,
      scopeKey: [currentTagId ?? '', ...extraNetworkTagIds].join('|'),
      scopeReady: authenticated && !networksLoading,
    }),
    [
      networks,
      currentNetwork,
      networksLoading,
      detailLoading,
      switchNetwork,
      extraNetworkTagIds,
      extraNetworks,
      toggleExtraNetwork,
      clearExtraNetworks,
      currentTagId,
      authenticated,
    ],
  )

  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>
}

export function useNetwork() {
  return useContext(NetworkContext)
}
